import type { ErrorResponse } from '../types/rest';
import { getErrorMessage } from '../types/rest';
import type { FetchError } from './http.types';

/** Safely trims only if the value is a string; returns undefined for non-strings and empty results. */
function toTrimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  return value.trim() || undefined;
}

/**
 * Extract a message from a plain-text or JSON error body.
 *
 * Plain-text error body, or `message` / `error_msg` from JSON (Talos {@link ErrorResponse} shape).
 * Does not return raw stringified JSON when the payload has no extractable message (falls through to status in {@link getFetchErrorUserMessage}).
 *
 * Ava server codebase almost always returns plain-text error bodies, so this function is primarily used to extract that message.
 * - In some small cases, a JSON object is returned with a `message` or `error_msg` field, so if that's seen, it'll return it.
 * - If need be, we're evolve this to provide stronger JSON result support.
 */
export function getMessageFromFetchErrorBody(body: string | undefined): string | undefined {
  const trimmed = body?.trim() || undefined;
  if (!trimmed) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'string') {
      const t = parsed.trim();
      return t || undefined;
    }
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const msg = toTrimmedString(getErrorMessage(parsed as ErrorResponse));
      return msg || undefined;
    }
    return undefined;
  } catch {
    return trimmed;
  }
}

/**
 * User-facing message from a failed HTTP request (no URL).
 * Prefer errorJson → plain text body or message from JSON → status + statusText → fallback.
 */
export function getFetchErrorUserMessage(error: FetchError, fallbackMessage = 'Request failed'): string {
  const fromErrorJson = toTrimmedString(getErrorMessage(error.errorJson));
  const fromBody = getMessageFromFetchErrorBody(error.body);
  const statusText = error.response?.statusText || '';
  const statusMessage =
    typeof error.response?.status === 'number'
      ? `${error.response.status}${statusText ? ` ${statusText}` : ''}`
      : undefined;
  return fromErrorJson ?? fromBody ?? statusMessage ?? fallbackMessage;
}
